import { useState, useEffect, useRef, useMemo } from "react";
import {
  useGetContactMessagesQuery,
  useMarkMessageReadMutation,
  useDeleteMessageMutation,
} from "../../features/api/adminApiSlice";
import FuelLoading from "../../pages/FuelLoading";
import "./adminMessageInfo.scss";
import { useToast } from "../toast/ToastContext";
import ConfirmModal from "../modal/ConfirmModal";
import AdminHeader from "../layout/AdminHeader";

export default function AdminMessagesInbox() {
  const {
    data: messages = [],
    isLoading,
    isError,
  } = useGetContactMessagesQuery();
  const [markMessageRead] = useMarkMessageReadMutation();
  const [deleteMessage] = useDeleteMessageMutation();

  const { showToast } = useToast();

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [viewMode, setViewMode] = useState("active");
  const prevCountRef = useRef(messages.length);

  // 🟢 NEW: MODAL DISPATCHER STATES FOR SECURE PURGES
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    targetId: null,
    targetSender: "",
  });

  // Live Notification Audio Ring Loop
  useEffect(() => {
    if (messages.length > prevCountRef.current) {
      const unreadAlert = messages[0];
      if (unreadAlert && !unreadAlert.isReviewed) {
        try {
          const audioCtx = new (window.AudioContext ||
            window.webkitAudioContext)();
          const oscillator = audioCtx.createOscillator();
          const gainNode = audioCtx.createGain();
          oscillator.type = "sine";
          oscillator.frequency.setValueAtTime(587.33, audioCtx.currentTime);
          gainNode.gain.setValueAtTime(0.08, audioCtx.currentTime);
          oscillator.connect(gainNode);
          gainNode.connect(audioCtx.destination);
          oscillator.start();
          oscillator.stop(audioCtx.currentTime + 0.15);

          // 🟢 Notify via Toast instantly when a new live message rolls in
          showToast(
            `New incoming transmission from ${unreadAlert.name}!`,
            "info"
          );
        } catch (audioError) {
          console.log("Audio notification block bypassed:", audioError);
        }
      }
    }
    prevCountRef.current = messages.length;
  }, [messages, showToast]);

  // Compute Base Metrics
  const totalCount = messages.length;
  const unreadCount = messages.filter((m) => !m.isReviewed).length;
  const archivedCount = messages.filter((m) => m.isReviewed).length;
  const tipCount = messages.filter((m) =>
    m.subject?.toLowerCase().includes("route")
  ).length;

  // Filter what cards appear inside the sidebar stack list dynamically based on viewMode
  const displayedMessages = useMemo(() => {
    if (viewMode === "archived") {
      return messages.filter((m) => m.isReviewed);
    }
    return messages.filter((m) => !m.isReviewed); // Default: Active Inbox (Unread Notes)
  }, [messages, viewMode]);

  // AUTOMATIC CLIENT-SIDE EXPORT TO CSV GENERATOR LOGS
  const handleExportCSV = () => {
    if (messages.length === 0) return;

    try {
      const headers = [
        "ID",
        "Name",
        "Email",
        "Subject",
        "Message",
        "Reviewed Status",
        "Logged Date",
      ];
      const rows = messages.map((msg) => [
        msg._id,
        `"${msg.name.replace(/"/g, '""')}"`,
        msg.email,
        `"${msg.subject.replace(/"/g, '""')}"`,
        `"${msg.message.replace(/"/g, '""')}"`,
        msg.isReviewed ? "Read" : "Unread",
        new Date(msg.createdAt).toISOString(),
      ]);

      const csvContent =
        "data:text/csv;charset=utf-8," +
        [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
      const encodedUri = encodeURI(csvContent);
      const linkElement = document.createElement("a");
      linkElement.setAttribute("href", encodedUri);
      linkElement.setAttribute(
        "download",
        `MiniMiles_Messages_Backup_${
          new Date().toISOString().split("T")[0]
        }.csv`
      );
      document.body.appendChild(linkElement);
      linkElement.click();
      document.body.removeChild(linkElement);

      // 🟢 Success Feedback Toast
      showToast(
        "Database spreadsheet backup downloaded successfully!",
        "success"
      );
    } catch (err) {
      showToast("Failed to compile CSV data backup.", "error");
    }
  };

  // Intercept raw deletion commands and redirect through local layout states modal
  const handleDeleteTrigger = (id, senderName) => {
    setModalConfig({ isOpen: true, targetId: id, targetSender: senderName });
  };

  const executeDeletePurge = async () => {
    const { targetId, targetSender } = modalConfig;
    setModalConfig({ isOpen: false, targetId: null, targetSender: "" });

    try {
      await deleteMessage(targetId).unwrap();
      setSelectedMessage(null);
      showToast(
        `Transmission log entry from "${targetSender}" successfully deleted.`,
        "success"
      );
    } catch (err) {
      showToast(
        "Failed to clear message record from backend channels.",
        "error"
      );
    }
  };

  if (isLoading) {
    return (
      <div className="admin-inbox-loading">
        <FuelLoading text="Opening mail trucks..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="admin-error">
        ⚠️ Secure entry denied or database connection offline.
      </div>
    );
  }

  return (
    <div className="admin-messages-wrapper fuel-fade-in">
      {/* 🟢 REUSABLE DYNAMIC CONFIRMATION MODAL */}
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        variant="warning"
        title="Delete Transmission Note"
        confirmText="Delete Note"
        message={`Are you absolutely certain you want to permanently erase the message history log from "${modalConfig.targetSender}"? This structural purge cannot be reverted.`}
        onConfirm={executeDeletePurge}
        onCancel={() =>
          setModalConfig({ isOpen: false, targetId: null, targetSender: "" })
        }
      />

      {/* 🟢 CENTRAL REUSABLE HEADER STRUCTURE INJECTED */}
      <AdminHeader
        title="Communications Central Terminal"
        description="Review travel route tips from readers, process direct inquiries, audit backlog logs, and compile offline file backups."
        badgeText="Inbox Hub"
      />

      {/* Analytics Summary Header Box Rows */}
      <header className="admin-stats-row">
        <div className="stat-box">
          <span className="stat-box__icon">📬</span>
          <div className="stat-box__info">
            <strong>{totalCount}</strong>
            <span>Total Logged</span>
          </div>
        </div>
        <div className="stat-box stat-box--alert">
          <span className="stat-box__icon">🚨</span>
          <div className="stat-box__info">
            <strong>{unreadCount}</strong>
            <span>Unread Notes</span>
          </div>
        </div>
        <div className="stat-box">
          <span className="stat-box__icon">🗺️</span>
          <div className="stat-box__info">
            <strong>{tipCount}</strong>
            <span>Route Tips</span>
          </div>
        </div>
        <div className="stat-box">
          <span className="stat-box__icon">💾</span>
          <div className="stat-box__info">
            {/* CSV Backup Link Button Execution */}
            <button
              type="button"
              className="csv-download-btn"
              onClick={handleExportCSV}
              disabled={totalCount === 0}
            >
              Backup CSV 📥
            </button>
            <span>Database Utilities</span>
          </div>
        </div>
      </header>

      <div className="admin-workspace">
        {/* LEFT PANEL: MESSAGE CHIPS LIST STACK */}
        <section className="admin-inbox-pane">
          <div className="pane-title-header">
            {/* FIXED: Segmented visual controls tabs layout to slide between Active/Read groups */}
            <div className="inbox-tabs">
              <button
                type="button"
                className={`inbox-tabs__btn ${
                  viewMode === "active" ? "active" : ""
                }`}
                onClick={() => {
                  setViewMode("active");
                  setSelectedMessage(null);
                }}
              >
                📥 Active Inbox ({unreadCount})
              </button>
              <button
                type="button"
                className={`inbox-tabs__btn ${
                  viewMode === "archived" ? "active" : ""
                }`}
                onClick={() => {
                  setViewMode("archived");
                  setSelectedMessage(null);
                }}
              >
                📁 Archive ({archivedCount})
              </button>
            </div>
          </div>

          <div className="messages-stack">
            {displayedMessages.length === 0 ? (
              <p className="empty-inbox-text">
                No {viewMode === "archived" ? "archived" : "unread"} messages to
                show here. 🏁
              </p>
            ) : (
              displayedMessages.map((msg) => (
                <button
                  key={msg._id}
                  type="button"
                  className={`msg-strip ${
                    !msg.isReviewed ? "msg-strip--unread" : ""
                  } ${
                    selectedMessage?._id === msg._id ? "msg-strip--active" : ""
                  }`}
                  onClick={() => {
                    setSelectedMessage(msg);
                    if (!msg.isReviewed) markMessageRead(msg._id);
                  }}
                >
                  <div className="msg-strip__top">
                    <strong>{msg.name}</strong>
                    <time>{new Date(msg.createdAt).toLocaleDateString()}</time>
                  </div>
                  <span className="msg-strip__subject">📌 {msg.subject}</span>
                  <p className="msg-strip__snippet">{msg.message}</p>
                </button>
              ))
            )}
          </div>
        </section>

        {/* RIGHT PANEL: FULL TEXT PREVIEW INTERACTIVE DETAILS CANVAS */}
        <section className="admin-preview-pane">
          {selectedMessage ? (
            <div
              className="message-detail-view fuel-fade-in"
              key={selectedMessage._id}
            >
              <div className="detail-header">
                <div className="sender-meta">
                  <h3>{selectedMessage.name}</h3>
                  <a href={`mailto:${selectedMessage.email}`}>
                    {selectedMessage.email}
                  </a>
                </div>
                <button
                  type="button"
                  className="delete-action-btn"
                  onClick={() =>
                    handleDeleteTrigger(
                      selectedMessage._id,
                      selectedMessage.name
                    )
                  }
                >
                  🗑️ Delete Note
                </button>
              </div>

              <div className="detail-meta-box">
                <p>
                  <strong>Logged On:</strong>{" "}
                  {new Date(selectedMessage.createdAt).toLocaleString()}
                </p>
                <p>
                  <strong>Topic / Tag:</strong> {selectedMessage.subject}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  {selectedMessage.isReviewed ? "📁 Archived" : "🚨 Unread"}
                </p>
              </div>

              <div className="detail-body">
                <h4>Message Body:</h4>
                <p>“{selectedMessage.message}”</p>
              </div>
            </div>
          ) : (
            <div className="pane-placeholder">
              <span className="placeholder-icon">📬</span>
              <p>
                Select a road note from the inbox feed panel to open and review
                details.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
